import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Popover,
    IconButton,
    InputAdornment
} from "@mui/material";
import { Filter, RotateCcw, Calendar as CalendarIcon, ChevronDown, X, Search } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axiosInstance from "../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../services/apiEndPoints";
import { DISPOSITION_OPTIONS, SUB_DISPOSITION_OPTIONS } from "../utils/constants";



const chipSx = (active) => ({
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 30,
    borderRadius: '8px',
    border: '1px solid',
    borderColor: active ? '#6366F1' : '#E5E7EB',
    bgcolor: active ? '#EEF2FF' : '#fff',
    color: active ? '#4F46E5' : '#6B7280',
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': { borderColor: '#6366F1', bgcolor: '#F5F7FF' },
});

const LeadFilters = ({ filters, setFilters, userId, stages }) => {
    const [managers, setManagers] = useState([]);
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const [followUpCalendarAnchor, setFollowUpCalendarAnchor] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [categories, setCategories] = useState([]);
    const [searchText, setSearchText] = useState(filters.search || "");
    const userRole = localStorage.getItem("userRole");

    // Helper: YYYY-MM-DD in local time
    const formatLocalDate = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Debounced search — waits 400ms after typing before updating filters
    useEffect(() => {
        const timer = setTimeout(() => {
            handleChange("search", searchText);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        fetchManagers();
        fetchCategories();
    }, []);

    // Auto-show advanced row if any advanced filter is already set
    useEffect(() => {
        if (filters.interest || filters.disposition || filters.subDisposition || filters.whatsappSent || filters.followUpStart || filters.pocIdentified) {
            setShowAdvanced(true);
        }
    }, [filters]);

    const fetchManagers = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`);
            if (res.data.success) {
                setManagers(res.data.data.filter(m => m.role === "account manager"));
            }
        } catch (error) {
            console.error("Error fetching managers:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getCategories}/${userId}`);
            if (res.data.success) setCategories(res.data.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleChange = (field, value) => {
        const next = { ...filters, [field]: value };
        // Remove empty values
        Object.keys(next).forEach(k => { if (next[k] === "" || next[k] === undefined) delete next[k]; });
        setFilters(next);
    };

    const handleClear = () => {
        setFilters({});
        setShowAdvanced(false);
    };

    const selectedDateRange = [
        filters.startDate ? new Date(filters.startDate) : null,
        filters.endDate ? new Date(filters.endDate) : null
    ];

    const selectedFollowUpRange = [
        filters.followUpStart ? new Date(filters.followUpStart) : null,
        filters.followUpEnd ? new Date(filters.followUpEnd) : null
    ];

    const clearFollowUpDates = (e) => {
        e.stopPropagation();
        const next = { ...filters };
        delete next.followUpStart;
        delete next.followUpEnd;
        setFilters(next);
    };

    const clearDates = (e) => {
        e.stopPropagation();
        const next = { ...filters };
        delete next.startDate;
        delete next.endDate;
        setFilters(next);
    };

    const handleQuickDate = (type) => {
        const today = new Date();
        if (type === "today") {
            const dateStr = formatLocalDate(today);
            setFilters({ ...filters, startDate: dateStr, endDate: dateStr });
        } else if (type === "yesterday") {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            const dateStr = formatLocalDate(yesterday);
            setFilters({ ...filters, startDate: dateStr, endDate: dateStr });
        }
    };

    const activeCount = Object.keys(filters).length;

    return (
        <Box sx={{ mb: 2 }}>
            {/* ===== ROW 1: Main Filters ===== */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                p: 1.5,
                bgcolor: '#FAFBFC',
                borderRadius: '12px',
                border: '1px solid #F0F0F3',
            }}>
                {/* Filter Icon + Label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
                    <Filter size={15} color="#6366F1" />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366F1', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                        FILTERS
                    </Typography>
                    {activeCount > 0 && (
                        <Chip label={activeCount} size="small" sx={{ height: 18, minWidth: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#6366F1', color: '#fff', ml: 0.5 }} />
                    )}
                </Box>

                {/* Search by Name / Phone */}
                <TextField
                    size="small"
                    placeholder="Search name, company or phone…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={14} color="#94a3b8" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        minWidth: 180,
                        '& .MuiInputBase-root': { borderRadius: '8px', height: 32, fontSize: '0.75rem', bgcolor: '#fff' },
                        '& .MuiInputBase-input': { py: 0.5 }
                    }}
                />

                {/* Quick Date Filters */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip
                        label="Today"
                        size="small"
                        onClick={() => handleQuickDate("today")}
                        sx={chipSx(filters.startDate === formatLocalDate(new Date()) && filters.endDate === formatLocalDate(new Date()))}
                    />
                    <Chip
                        label="Yesterday"
                        size="small"
                        onClick={() => handleQuickDate("yesterday")}
                        sx={chipSx(filters.startDate === formatLocalDate(new Date(Date.now() - 86400000)) && filters.endDate === formatLocalDate(new Date(Date.now() - 86400000)))}
                    />
                </Box>

                {/* Date Range Picker */}
                <Box
                    onClick={(e) => setCalendarAnchor(e.currentTarget)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1.5, py: 0.6,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: (filters.startDate && filters.endDate) ? '#6366F1' : '#E5E7EB',
                        bgcolor: (filters.startDate && filters.endDate) ? '#EEF2FF' : '#fff',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: (filters.startDate && filters.endDate) ? '#4F46E5' : '#6B7280',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: '#6366F1' },
                    }}
                >
                    <CalendarIcon size={13} />
                    {filters.startDate && filters.endDate ? (
                        <>
                            {new Date(filters.startDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – {new Date(filters.endDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                            <IconButton size="small" onClick={clearDates} sx={{ p: 0, ml: 0.3 }}><X size={12} /></IconButton>
                        </>
                    ) : "Date Range"}
                </Box>

                <Popover
                    open={Boolean(calendarAnchor)}
                    anchorEl={calendarAnchor}
                    onClose={() => setCalendarAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    PaperProps={{ sx: { mt: 1, borderRadius: 3, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', border: '1px solid #F3F4F6' } }}
                >
                    <Box sx={{ p: 1 }}>
                        <Calendar
                            selectRange={true}
                            value={selectedDateRange[0] ? selectedDateRange : null}
                            onChange={(range) => {
                                if (range && range.length === 2) {
                                    const next = { 
                                        ...filters, 
                                        startDate: formatLocalDate(range[0]), 
                                        endDate: formatLocalDate(range[1]) 
                                    };
                                    setFilters(next);
                                    setCalendarAnchor(null);
                                }
                            }}
                        />
                    </Box>
                </Popover>

                {/* Stage Dropdown */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                        value={filters.stage || ""}
                        onChange={(e) => handleChange("stage", e.target.value)}
                        displayEmpty
                        sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 32, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.5 } }}
                    >
                        <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Stages</MenuItem>
                        {stages.map(s => <MenuItem key={s._id} value={s._id} sx={{ fontSize: '0.8rem' }}>{s.name}</MenuItem>)}
                    </Select>
                </FormControl>

                {/* Assignee Dropdown - Hidden for Account Managers */}
                {userRole !== "account manager" && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={filters.assignee || ""}
                            onChange={(e) => handleChange("assignee", e.target.value)}
                            displayEmpty
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 32, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.5 } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Assignees</MenuItem>
                            <MenuItem value="unassigned" sx={{ fontSize: '0.8rem' }}>Unassigned</MenuItem>
                            {managers.map(m => <MenuItem key={m._id} value={m._id} sx={{ fontSize: '0.8rem' }}>{m.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}

                {/* Priority Chips */}
                {["High", "Medium", "Low"].map((p) => (
                    <Chip
                        key={p}
                        label={p}
                        size="small"
                        onClick={() => handleChange("priority", filters.priority === p ? "" : p)}
                        sx={chipSx(filters.priority === p)}
                    />
                ))}



                {/* Advanced Toggle */}
                <Chip
                    label={showAdvanced ? "▲ Less" : "⚡ More"}
                    size="small"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    sx={{
                        fontWeight: 700, fontSize: '0.7rem', height: 30, borderRadius: '8px',
                        bgcolor: showAdvanced ? '#6366F1' : '#F5F3FF',
                        color: showAdvanced ? '#fff' : '#6366F1',
                        border: '1px solid #6366F1',
                        '&:hover': { bgcolor: showAdvanced ? '#4F46E5' : '#EDE9FE' },
                    }}
                />

                {/* Clear All */}
                {activeCount > 0 && (
                    <Button
                        size="small"
                        onClick={handleClear}
                        startIcon={<RotateCcw size={13} />}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', color: '#EF4444', ml: 'auto', minWidth: 'auto' }}
                    >
                        Clear
                    </Button>
                )}
            </Box>

            {/* ===== ROW 2: Advanced Filters (Collapsible) ===== */}
            {showAdvanced && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    mt: 1,
                    p: 1.5,
                    bgcolor: '#F5F3FF',
                    borderRadius: '12px',
                    border: '1px solid #E9E5FF',
                }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366F1', fontSize: '0.68rem', letterSpacing: '0.05em', mr: 0.5 }}>
                        CLIENT Details
                    </Typography>

                    {/* Category */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={filters.interest || ""}
                            onChange={(e) => handleChange("interest", e.target.value)}
                            displayEmpty
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 30, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.3 } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Category: All</MenuItem>
                            {categories.map(cat => <MenuItem key={cat._id} value={cat.name} sx={{ fontSize: '0.8rem' }}>{cat.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Disposition */}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={filters.disposition || ""}
                            onChange={(e) => handleChange("disposition", e.target.value)}
                            displayEmpty
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 30, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.3 } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Disposition: All</MenuItem>
                            {DISPOSITION_OPTIONS.map(opt => <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>{opt}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* Sub Disposition */}
                    <FormControl size="small" sx={{ minWidth: 145 }}>
                        <Select
                            value={filters.subDisposition || ""}
                            onChange={(e) => handleChange("subDisposition", e.target.value)}
                            displayEmpty
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 30, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.3 } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Sub Disposition: All</MenuItem>
                            {SUB_DISPOSITION_OPTIONS.map(opt => <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>{opt}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* POC Identified */}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={filters.pocIdentified || ""}
                            onChange={(e) => handleChange("pocIdentified", e.target.value)}
                            displayEmpty
                            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, height: 30, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.3 } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>POC: All</MenuItem>
                            <MenuItem value="yes" sx={{ fontSize: '0.8rem' }}>POC: Yes</MenuItem>
                            <MenuItem value="no" sx={{ fontSize: '0.8rem' }}>POC: No</MenuItem>
                        </Select>
                    </FormControl>

                    {/* WhatsApp Chips */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.7rem', mr: 0.3 }}>WA:</Typography>
                        {[
                            { label: "✅ Yes", value: "true" },
                            { label: "No", value: "false" },
                        ].map((opt) => (
                            <Chip
                                key={opt.label}
                                label={opt.label}
                                size="small"
                                onClick={() => handleChange("whatsappSent", filters.whatsappSent === opt.value ? "" : opt.value)}
                                sx={{
                                    ...chipSx(filters.whatsappSent === opt.value),
                                    height: 26,
                                    fontSize: '0.68rem',
                                    borderColor: filters.whatsappSent === opt.value ? '#22C55E' : '#E5E7EB',
                                    bgcolor: filters.whatsappSent === opt.value ? '#F0FDF4' : '#fff',
                                    color: filters.whatsappSent === opt.value ? '#16A34A' : '#6B7280',
                                }}
                            />
                        ))}
                    </Box>

                    {/* Follow Up Date Range */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.7rem', mr: 0.3 }}>Follow Up:</Typography>
                        <Box
                            onClick={(e) => setFollowUpCalendarAnchor(e.currentTarget)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                px: 1.5, py: 0.4,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: (filters.followUpStart && filters.followUpEnd) ? '#F59E0B' : '#E5E7EB',
                                bgcolor: (filters.followUpStart && filters.followUpEnd) ? '#FFFBEB' : '#fff',
                                cursor: 'pointer',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: (filters.followUpStart && filters.followUpEnd) ? '#D97706' : '#6B7280',
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: '#F59E0B' },
                                height: 26,
                            }}
                        >
                            <CalendarIcon size={12} />
                            {filters.followUpStart && filters.followUpEnd ? (
                                <>
                                    {new Date(filters.followUpStart).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – {new Date(filters.followUpEnd).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                                    <IconButton size="small" onClick={clearFollowUpDates} sx={{ p: 0, ml: 0.3 }}><X size={11} /></IconButton>
                                </>
                            ) : "Select Range"}
                        </Box>

                        <Popover
                            open={Boolean(followUpCalendarAnchor)}
                            anchorEl={followUpCalendarAnchor}
                            onClose={() => setFollowUpCalendarAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            PaperProps={{ sx: { mt: 1, borderRadius: 3, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', border: '1px solid #F3F4F6' } }}
                        >
                            <Box sx={{ p: 1 }}>
                                <Calendar
                                    selectRange={true}
                                    value={selectedFollowUpRange[0] ? selectedFollowUpRange : null}
                                    onChange={(range) => {
                                        if (range && range.length === 2) {
                                            const next = { 
                                                ...filters, 
                                                followUpStart: formatLocalDate(range[0]), 
                                                followUpEnd: formatLocalDate(range[1]) 
                                            };
                                            setFilters(next);
                                            setFollowUpCalendarAnchor(null);
                                        }
                                    }}
                                />
                            </Box>
                        </Popover>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LeadFilters;
